import { EnrichedWarrantSingleItem } from '../types/WarrantEntity'
import {
    GetWarrantByIdRequest,
    GetWarrantListRequest,
    GetWarrantsByCompanyIdRequest,
    GetWarrantsByIssueIdRequest,
    PatchWarrantRequest,
    PostWarrantRequest,
    PutWarrantRequest,
    WarrantSubscriptionPeriod,
} from '@finance-lab/utils/types'
import { findSubscriptionPeriodCloseToDate } from '../utils/warrants'
import ApiBase from './Api'
import { getStoredLanguage, resolveLanguage } from '../i18n/languagePreference'

const WARRANT_API_PATHS = {
    WARRANTS: '/warrants',
    WARRANT: (id: string) => `/warrants/${id}`,
    ADMIN_WARRANTS: '/admin/warrants',
    ADMIN_WARRANT: (id: string) => `/admin/warrants/${id}`,
}

const sortSubscriptionPeriods = (
    a: Pick<WarrantSubscriptionPeriod, 'subStartDate'>,
    b: Pick<WarrantSubscriptionPeriod, 'subStartDate'>,
) =>
    a.subStartDate === null
        ? 1
        : b.subStartDate === null
          ? -1
          : new Date(a.subStartDate).getTime() - new Date(b.subStartDate).getTime()

class WarrantApi extends ApiBase {
    async findAll() {
        const warrants = await this._getRequest<GetWarrantListRequest>(WARRANT_API_PATHS.WARRANTS)
        return warrants
    }

    async findByWarrantId(warrantId: string) {
        if (!warrantId) {
            return null
        }
        const preferredLanguage = resolveLanguage(getStoredLanguage())
        const warrant = await this._getRequest<GetWarrantByIdRequest>(WARRANT_API_PATHS.WARRANT(warrantId), {
            queryParameters: { preferredLanguage },
        })

        const currentPeriod = findSubscriptionPeriodCloseToDate(warrant, new Date().toISOString())

        return {
            ...warrant,
            subscriptionPeriods: warrant.subscriptionPeriods.sort(sortSubscriptionPeriods),
            currentSubscriptionPeriod: currentPeriod,
        } as EnrichedWarrantSingleItem
    }

    async findByCompanyId(companyId: string, { excludeCancelled = false }: { excludeCancelled?: boolean } = {}) {
        if (!companyId) {
            return []
        }
        const warrants = await this._getRequest<GetWarrantsByCompanyIdRequest>(WARRANT_API_PATHS.WARRANTS, {
            queryParameters: {
                companyId,
                ...(excludeCancelled ? { excludeCancelled: 'true' } : {}),
            },
        })

        return warrants
    }

    async findByIssueId(issueId: string) {
        if (!issueId) {
            return []
        }
        const warrants = await this._getRequest<GetWarrantsByIssueIdRequest>(WARRANT_API_PATHS.WARRANTS, {
            queryParameters: { issueId },
        })

        return warrants
    }

    async create(data: PostWarrantRequest['Body']) {
        if (!data) {
            return null
        }

        const warrant = await this._postRequest<PostWarrantRequest>(WARRANT_API_PATHS.WARRANTS, data)

        return warrant
    }

    async update(warrantId: string, data: PutWarrantRequest['Body']) {
        if (!data) {
            return null
        }
        const updatedWarrant = await this._putRequest<PutWarrantRequest>(
            WARRANT_API_PATHS.ADMIN_WARRANT(warrantId),
            data,
        )
        return updatedWarrant
    }

    /**
     * Changes specific fields of a warrant. The difference between this method and the update method is that this method
     * only updates the fields that are passed in the data object. The update method updates all fields.
     * Note that only certain fields are allowed to be updated with the PATCH method. Currently only the isinWarrant
     * field is allowed.
     */
    async patchFields(warrantId: string, data: PatchWarrantRequest['Body']) {
        if (!data || Object.keys(data).length === 0) {
            return null
        }
        const fieldsToUpdate = { isinWarrant: data['isinWarrant'] }
        if (Object.keys(fieldsToUpdate).length === 0) {
            console.error('No whitelisted fields to update. ', data)
            return null
        }
        const updatedWarrant = await this._patchRequest<PatchWarrantRequest>(
            WARRANT_API_PATHS.ADMIN_WARRANT(warrantId),
            fieldsToUpdate,
        )
        return updatedWarrant
    }

    async delete(warrantId: string) {
        if (!warrantId) {
            return null
        }

        return this._deleteRequest(WARRANT_API_PATHS.ADMIN_WARRANT(warrantId))
    }
}

export default WarrantApi
